import api from './api';
import { downloadFromApi } from './download';

/**
 * Import de camions et de chauffeurs depuis un fichier Excel.
 *
 * Chemins et formes de réponse : `backend/app/api/v1/fleet_import.py`. Rien n'est
 * calculé ici : le brouillon, les états et les phrases de refus viennent du
 * serveur. Les travaux sont cloisonnés par société côté serveur ; le super
 * administrateur passe `organizationId` pour choisir la société cible.
 */

const BASE = '/fleet-import';
// Le serveur répond `{ success, data }` : `data` vaut `null` quand il n'y a rien (aucun import en
// cours). Ce `null` est la réponse, il ne faut pas retomber sur l'enveloppe entière.
const unwrap = (response) => {
  const corps = response?.data;
  return corps && typeof corps === 'object' && !Array.isArray(corps) && 'data' in corps ? corps.data : corps;
};

export const fleetImportApi = {
  /** Le travail en cours de la société (ou le dernier terminé), ou `null`. */
  async current(organizationId) {
    const params = organizationId ? { organization_id: organizationId } : {};
    return unwrap(await api.get(`${BASE}/jobs/current`, { params })) || null;
  },

  async get(jobId) {
    return unwrap(await api.get(`${BASE}/jobs/${jobId}`));
  },

  /** Dépose le fichier ; l'analyse se poursuit en arrière-plan. */
  async upload(file, organizationId) {
    const formulaire = new FormData();
    formulaire.append('file', file);
    if (organizationId) formulaire.append('organization_id', organizationId);
    return unwrap(
      await api.post(`${BASE}/jobs`, formulaire, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      }),
    );
  },

  async rows(jobId, { kind, state, q, page = 1, pageSize = 50 } = {}) {
    const params = { page, page_size: pageSize };
    if (kind) params.kind = kind;
    if (state) params.state = state;
    if (q) params.q = q;
    return unwrap(await api.get(`${BASE}/jobs/${jobId}/rows`, { params }));
  },

  async reportLines(jobId, { status, page = 1, pageSize = 50 } = {}) {
    const params = { page, page_size: pageSize };
    if (status) params.status = status;
    return unwrap(await api.get(`${BASE}/jobs/${jobId}/report-lines`, { params }));
  },

  async updateMapping(jobId, entries) {
    return unwrap(await api.put(`${BASE}/jobs/${jobId}/mapping`, { entries }));
  },

  /** Corrige des cellules (`values`) et/ou coche ou décoche la ligne (`included`). */
  async editRow(jobId, key, { values, included } = {}) {
    const corps = { key };
    if (values) corps.values = values;
    if (included !== undefined) corps.included = included;
    return unwrap(await api.patch(`${BASE}/jobs/${jobId}/rows`, corps));
  },

  async select(jobId, { included, kind, state, keys }) {
    const corps = { included };
    if (kind) corps.kind = kind;
    if (state) corps.state = state;
    if (keys) corps.keys = keys;
    return unwrap(await api.post(`${BASE}/jobs/${jobId}/selection`, corps));
  },

  /** Confirme l'import ; renvoyer deux fois ne relance rien. */
  async confirm(jobId, draftVersion) {
    return unwrap(await api.post(`${BASE}/jobs/${jobId}/confirm`, { draft_version: draftVersion }));
  },

  async cancel(jobId) {
    return unwrap(await api.post(`${BASE}/jobs/${jobId}/cancel`));
  },

  async template() {
    return downloadFromApi(`${BASE}/template`, {}, 'import-flotte-modele.xlsx');
  },

  async report(jobId) {
    return downloadFromApi(`${BASE}/jobs/${jobId}/report`, {}, 'rapport-import-flotte.xlsx');
  },
};

export default fleetImportApi;
